import { createClient } from "@connectrpc/connect";
import type { MultiPolygon } from "geojson";
import {
  BaseGrpcCatalogService,
  GrpcCatalogoFetchResult,
} from "../../../../base/services/BaseGrpcCatalogService";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../../comun/services/grpc/mapConnectError";
import { wkbToMultiPolygon } from "../../../../utils/wkbUtils";
import {
  CatalogosTercerosService,
  DepartamentoItem,
} from "../../../../generated/grpc/catalogos_terceros_pb";

/** Departamento tal como queda guardado: `areaGeografica` ya decodificada de
 *  WKB a GeoJSON (mismo shape que usa REST en `DepartamentoResponse`), una
 *  sola vez acá — nada más en la app necesita enterarse de que el transporte
 *  cambió. Mismo criterio que `StoredFinca`/`StoredLote` en `GrpcFincaLoteDatabase`. */
export type StoredDepartamento = Omit<DepartamentoItem, "areaGeografica"> & {
  areaGeografica: MultiPolygon;
};

/** Variante gRPC-Web (con delta) de `DepartamentoGetCatalogService` (ver ese archivo). */
export class DepartamentoGrpcCatalogService extends BaseGrpcCatalogService<StoredDepartamento> {
  protected readonly storeName = "departamentos" as const;
  protected readonly sortFieldName = "nombre";

  protected async fetchAsync(ultimaSincronizacion: bigint): Promise<GrpcCatalogoFetchResult<StoredDepartamento>> {
    const client = createClient(CatalogosTercerosService, grpcTransportProvider.get());

    try {
      const response = await client.listDepartamentos({ ultimaSincronizacion });
      const items: StoredDepartamento[] = response.items.map((item) => ({
        ...item,
        areaGeografica: wkbToMultiPolygon(item.areaGeografica),
      }));
      return { items, timestampSincronizacion: response.timestampSincronizacion };
    } catch (error) {
      throw mapConnectError(error);
    }
  }
}

/** Singleton del servicio gRPC del catálogo de departamentos. */
export const departamentoGrpcCatalogService = new DepartamentoGrpcCatalogService();
